import React, { useState } from 'react';
import { Code, Copy, Check, ChevronDown, ChevronUp, Database, Server, Smartphone } from 'lucide-react';

const codeSnippets = [
  {
    id: 'schema',
    title: 'Database Schema',
    description: 'MongoDB collections for users, lectures, and settings',
    language: 'javascript',
    icon: <Database className="w-5 h-5" />,
    code: `// MongoDB Collections Schema

// Users collection
{
  id: "uuid",
  email: "user@example.com",
  name: "John Doe",
  subscription_tier: "free",
  created_at: ISODate("2024-01-01")
}

// User preferences collection
{
  user_id: "uuid",
  theme: "light",
  font_size: 16,
  local_only_mode: false,
  accent_color: "#1A4D2E"
}

// Lectures collection
{
  id: "uuid",
  user_id: "uuid",
  subject_id: "uuid",
  title: "Machine Learning 101",
  transcript: "...",
  summary: "...",
  cornell_notes: { ... },
  created_at: ISODate("2024-01-01")
}`
  },
  {
    id: 'api',
    title: 'AI Service Endpoint',
    description: 'FastAPI endpoint for generating Cornell notes',
    language: 'python',
    icon: <Server className="w-5 h-5" />,
    code: `# summarize_lecture.py
from emergentintegrations.llm.chat import LlmChat, UserMessage

async def summarize_lecture(transcript: str):
    chat = LlmChat(
        api_key=os.environ["EMERGENT_LLM_KEY"],
        session_id=str(uuid.uuid4()),
        system_message="""You are a teaching assistant.
Transform this lecture transcript into Cornell notes."""
    ).with_model("gemini", "gemini-2.5-flash")

    prompt = f"""Transform this transcript into Cornell notes:

{transcript}

Return JSON with: keyTerms, mainNotes, summary, quizQuestions"""

    response = await chat.send_message(
        UserMessage(text=prompt)
    )
    return json.loads(response)`
  },
  {
    id: 'simplify',
    title: 'Confusion Button Handler',
    description: 'AI-powered instant explanation generator',
    language: 'python',
    icon: <Server className="w-5 h-5" />,
    code: `# simplify_text.py
from emergentintegrations.llm.chat import LlmChat, UserMessage

async def simplify_text(transcript_snippet: str, timestamp: str):
    system_prompt = """You are a world-class teaching 
assistant. When a student presses the "Confusion Button," 
provide a simple, clear explanation in 2-3 sentences.
Use everyday language and analogies."""

    chat = LlmChat(
        api_key=os.environ["EMERGENT_LLM_KEY"],
        session_id=str(uuid.uuid4()),
        system_message=system_prompt
    ).with_model("gemini", "gemini-2.5-flash")

    response = await chat.send_message(
        UserMessage(text=f"Explain this simply: {transcript_snippet}")
    )
    
    return {"explanation": response, "timestamp": timestamp}`
  },
  {
    id: 'frontend',
    title: 'React Component',
    description: 'LectureRecorder with live transcription',
    language: 'jsx',
    icon: <Smartphone className="w-5 h-5" />,
    code: `// LectureRecorder.jsx
import { useState } from 'react';

export function LectureRecorder() {
  const [transcript, setTranscript] = useState([]);
  const [explanation, setExplanation] = useState('');

  const handleConfusion = async () => {
    const last30Seconds = transcript.slice(-500);
    const response = await fetch('/api/simplify-text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        transcript_snippet: last30Seconds 
      })
    });
    const data = await response.json();
    setExplanation(data.explanation);
  };

  return (
    <div className="flex-1 bg-slate-900 p-4">
      <div className="space-y-2">
        {transcript.map((line, i) => (
          <p key={line.text || i} className="text-white">{line.text}</p>
        ))}
      </div>
      
      <button 
        onClick={handleConfusion}
        className="bg-pink-500 p-4 rounded-xl mt-4"
      >
        I'm Confused - Explain This!
      </button>
    </div>
  );
}`
  },
  {
    id: 'settings',
    title: 'Settings Component',
    description: 'User customization with theme and font controls',
    language: 'jsx',
    icon: <Smartphone className="w-5 h-5" />,
    code: `// SettingsPanel.jsx
import { useState } from 'react';

export function SettingsPanel() {
  const [settings, setSettings] = useState({
    theme: 'dark',
    fontSize: 16,
    localOnlyMode: false
  });

  const updateSettings = async (updates) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    
    await fetch('/api/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Settings</h2>
      
      {/* Theme Selection */}
      <div className="mb-6">
        <label className="font-semibold mb-2">Theme</label>
        <div className="flex gap-2">
          {['light', 'dark', 'high-contrast'].map(theme => (
            <button
              key={theme}
              onClick={() => updateSettings({ theme })}
              className={\`px-4 py-2 rounded-lg \${
                settings.theme === theme 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-slate-200'
              }\`}
            >
              {theme}
            </button>
          ))}
        </div>
      </div>

      {/* Font Size Slider */}
      <div className="mb-6">
        <label>Font Size: {settings.fontSize}px</label>
        <input
          type="range"
          min={12}
          max={24}
          value={settings.fontSize}
          onChange={e => updateSettings({ 
            fontSize: Number(e.target.value) 
          })}
        />
      </div>
    </div>
  );
}`
  }
];

const CodeSnippets = () => {
  const [expandedSnippet, setExpandedSnippet] = useState('schema');
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = async (code, id) => {
    await navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleSnippet = (id) => {
    setExpandedSnippet(expandedSnippet === id ? '' : id);
  };

  return (
    <section id="docs" className="py-24 bg-slate-50" data-testid="docs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-slate-200 text-slate-700 rounded-full px-4 py-2 mb-4">
            <Code className="w-4 h-4" />
            <span className="text-sm font-medium">Developer Documentation</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Built for
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-slate-700 to-slate-900">
              Developers
            </span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Explore our code examples and integrate iStudyPro into your own applications.
          </p>
        </div>

        {/* Code snippets accordion */}
        <div className="max-w-4xl mx-auto space-y-4">
          {codeSnippets.map((snippet) => (
            <div
              key={snippet.id}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              data-testid={`code-snippet-${snippet.id}`}
            >
              {/* Header */}
              <button
                onClick={() => toggleSnippet(snippet.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
                    {snippet.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{snippet.title}</h3>
                    <p className="text-sm text-slate-500">{snippet.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded">
                    {snippet.language}
                  </span>
                  {expandedSnippet === snippet.id ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>

              {/* Code content */}
              <div className={`overflow-hidden transition-all duration-300 ${
                expandedSnippet === snippet.id ? 'max-h-[600px]' : 'max-h-0'
              }`}>
                <div className="relative">
                  <button
                    onClick={() => handleCopy(snippet.code, snippet.id)}
                    className="absolute top-4 right-4 p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors z-10"
                    data-testid={`copy-btn-${snippet.id}`}
                  >
                    {copiedId === snippet.id ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-300" />
                    )}
                  </button>
                  <pre className="bg-slate-900 p-4 overflow-x-auto">
                    <code className="text-sm text-slate-300 font-mono whitespace-pre">
                      {snippet.code}
                    </code>
                  </pre>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-slate-600 mb-4">Want to see the full documentation?</p>
          <button 
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            onClick={() => alert('Full API documentation coming soon!')}
          >
            <Code className="w-5 h-5" />
            View Full API Docs
          </button>
        </div>
      </div>
    </section>
  );
};

export default CodeSnippets;
